import {parseISO} from 'date-fns'
import {Space, Checkbox, Spin} from 'antd'

import styles from './Conditions.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import {User} from 'modules/users'
import {useOpen} from 'common/hooks'
import {
  Box,
  Block,
  Text,
  VerticalDivider,
  Button,
  IconButton,
  IconTitle,
  Modal,
  Overlay,
  Dropdown,
  File,
  UserAvatar,
  CalendarDate,
  DotsIcon,
  CalendarListIcon,
  PenEditThirdIcon,
} from 'common/components'
import {FileFromServer} from 'common/types'
import {ConditionForm, useApproveProjectCondition} from '..'

interface ConditionsProps {
  requestView?: boolean
  request: any
  isRefetching: boolean
}

interface ListProps {
  conditions: any[]
  isRefetching: boolean
  borrower?: User
  disabled: boolean
}

interface ConditionProps {
  condition: any
  isRefetching: boolean
  borrower?: User
  disabled: boolean
}

interface ConditionDocumentsProps {
  documents: FileFromServer[]
}

export default function Conditions({
  requestView,
  request,
  isRefetching,
}: ConditionsProps) {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const [isOpen, handleOpen, handleClose] = useOpen()

  const conditions = request?.project?.conditions ?? []
  const disabled = !permissions?.includes('add conditions')
  const borrower = request.project?.borrower

  const content = (
    <div>
      <List
        conditions={conditions}
        isRefetching={isRefetching}
        borrower={borrower}
        disabled={disabled}
      />
      {conditions.length ? (
        !disabled && (
          <Box mt={24}>
            <Button variant="secondary" onClick={handleOpen}>
              add condition
            </Button>
          </Box>
        )
      ) : (
        <Button
          block
          className={styles.button}
          variant="text"
          disabled={disabled}
          onClick={handleOpen}
        >
          <div className={styles.check}>
            <div className={styles.plug} />
          </div>
          <div className={styles.date}>
            <CalendarListIcon />
          </div>
          <VerticalDivider margin="small" />
          <p>Add condition</p>
        </Button>
      )}
    </div>
  )

  return (
    <>
      {requestView ? (
        <Block className={styles.root} type="white">
          <IconTitle icon={<PenEditThirdIcon />} mb={16}>
            Conditions
          </IconTitle>
          {content}
        </Block>
      ) : (
        content
      )}
      {!disabled && (
        <Modal
          title="Add new condition"
          width={486}
          open={isOpen}
          onCancel={handleClose}
        >
          <ConditionForm request={request} onClose={handleClose} />
        </Modal>
      )}
    </>
  )
}

const List = ({conditions, isRefetching, borrower, disabled}: ListProps) => {
  const sortedConditions = conditions ? conditions.slice() : []
  sortedConditions.sort(
    (a: any, b: any) => +parseISO(a.date) - +parseISO(b.date)
  )

  return (
    <ul>
      {sortedConditions.map((condition: any) => (
        <Condition
          key={condition.uuid}
          condition={condition}
          isRefetching={isRefetching}
          borrower={borrower}
          disabled={disabled}
        />
      ))}
    </ul>
  )
}

const Condition = ({
  condition,
  isRefetching,
  borrower,
  disabled,
}: ConditionProps) => {
  const {
    mutate: approveProjectCondition,
    isMutating,
    isSuccess,
  } = useApproveProjectCondition(condition.uuid)
  const {createdBy} = condition
  const date = parseISO(condition.date)
  const checked = isSuccess || Boolean(condition.checkedDate)
  const isOverdue = !checked && date < new Date()
  const disableCheck =
    disabled || (!isMutating && Boolean(condition.checkedDate))

  const handleChange =
    isRefetching || checked || isMutating
      ? undefined
      : () => approveProjectCondition()

  return (
    <li className={styles.item}>
      <div className={styles.check}>
        {isMutating ? (
          <Spin size="small" />
        ) : (
          <Checkbox
            className={handleChange ? undefined : styles.notClickable}
            checked={checked}
            disabled={disableCheck}
            onChange={handleChange}
          />
        )}
      </div>
      <CalendarDate error={isOverdue} date={date} />
      <VerticalDivider margin="small" />
      <div className={styles.content}>
        <div className={styles.block}>
          <Text
            mb={6}
            cross={checked}
            color={isOverdue ? 'redError' : undefined}
          >
            {condition.description}
          </Text>
          <Space size={12}>
            <Space size={6}>
              <UserAvatar
                src={createdBy.photo}
                size={18}
                alt={createdBy.firstName}
              />
              <Text as="span" size="2xs" weight="medium" color="grey">
                Created by {createdBy.firstName}
              </Text>
            </Space>
            {condition.borrowerVisibility && (
              <Space size={6}>
                <UserAvatar
                  src={borrower?.photo}
                  size={18}
                  alt={borrower ? borrower.firstName : 'Borrower'}
                />
                <Text as="span" size="2xs" weight="medium">
                  Borrower's
                </Text>
              </Space>
            )}
          </Space>
        </div>
        <ConditionDocuments documents={condition.documents} />
      </div>
    </li>
  )
}

const ConditionDocuments = ({documents}: ConditionDocumentsProps) => {
  if (!documents || !documents.length) return null

  return (
    <Dropdown
      dropdownRender={() => (
        <Overlay>
          <ul className={styles.documents}>
            {documents.map((document: FileFromServer) => (
              <li key={document.uuid}>
                <File
                  name={document.name}
                  size={document.size}
                  url={document.url}
                />
              </li>
            ))}
          </ul>
        </Overlay>
      )}
      placement="bottomRight"
    >
      <IconButton
        className={styles.documentsButton}
        icon={<DotsIcon />}
        aria-label="Open documents"
      />
    </Dropdown>
  )
}
